import { useEffect, useState } from "react";
import { Badge, Dropdown } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import "./GridCustomBase.css";

const GridCustomBase = (props) => {

    const { t } = useTranslation();
    const { children, pageable, setPage, showLoading, setSize, height = 700, selectedCount = 0, selectionMode = 'multiple' } = props;
    const [pagination, setPagination] = useState([]);
    const [rowsPerPageText, setRowsPerPageText] = useState(null);
    const [internalLoading, setInternalLoading] = useState(true);
    const [previousPageable, setPreviousPageable] = useState(null);

    // Fluxo que limita a exibição das páginas.
    // Limite de páginas que serão exibidas
    let limitPages = 5;

    const nextPage = () => {
        let page = pageable.number + 1;
        setPage(page);
    }

    // Cria a paginação.
    const createPagination = () => {
        let pagination = [];

        // Intervalo de páginas que é exibido para o usuário.
        let startPage = Math.floor(pageable.number / limitPages) * limitPages;
        let endPage = Math.min((startPage + limitPages), pageable.totalPages);

        // Garante a exibição do número de páginas baseado no limitPages
        if ((startPage + 1) > limitPages) {
            startPage = (startPage - limitPages) + 2;
        }

        pagination.push(
            <li className={"page-item " + (pageable.first ? "disabled" : "")}>
                <a href="#" className="page-link" onClick={(e) => { if (!pageable.first) { setPage(0); e.preventDefault(); } }}>
                    <i className="fas fa-chevron-double-left"></i>
                </a>
            </li>
        );
        pagination.push(
            <li key="previous" className={"page-item previous-button " + (pageable.first ? "disabled" : "")}>
                <a href="#" className="page-link previous-button" onClick={(e) => { if (!pageable.first) { setPage(pageable.number - 1); e.preventDefault(); } }}><i className="fas fa-chevron-left"></i></a>
            </li>
        );

        for (let i = startPage; i < endPage; i++) {
            pagination.push(
                <li key={i} className={"page-item " + (pageable.number == i ? "active" : "")}>
                    <a href="#" className="page-link" onClick={(e) => { setPage(i); e.preventDefault(); }}>{i + 1}</a>
                </li>
            );
        }

        pagination.push(
            <li key="next" className={"page-item " + (pageable.last ? "page-item disabled" : "")}>
                <a href="#" className="page-link" onClick={(e) => { if (!pageable.last) { nextPage(); e.preventDefault(); } }}><i className="fas fa-chevron-right"></i></a>
            </li>
        );
        pagination.push(
            <li key="last" className={"page-item " + (pageable.last ? "disabled" : "")}>
                { /* Permite clicar quando não for a última página. A próxima página deve ser a soma da página atual em 'page' + 1. */}
                <a href="#" className="page-link" onClick={(e) => { if (!pageable.last) { setPage(pageable.totalPages - 1); e.preventDefault(); } }}><i className="fas fa-chevron-double-right"></i></a>
            </li>
        );

        setPagination(pagination);
    }

    const showPages = () => {
        return pageable && pageable.totalElements > 0;
    }

    // Gerencia o loading automaticamente
    // Mostra loading quando:
    // 1. pageable é null/undefined (primeira carga)
    // 2. showLoading está explicitamente true
    useEffect(() => {
        if (pageable === null || pageable === undefined) {
            setInternalLoading(true);
        } else {
            setInternalLoading(false);
            setPreviousPageable(pageable);
        }
    }, [pageable]);

    useEffect(() => {
        if (pageable != null) {
            createPagination();
        }
    }, [pageable]);

    // Define se deve mostrar o loading
    // Mostra loading se:
    // - showLoading prop for true OU
    // - showLoading não foi definido E internalLoading é true
    const shouldShowLoading = showLoading || (showLoading === undefined && internalLoading);

    return (
        <>
            <div className="row">
                <div className="col-xl-12">
                    <div className="grid-custom-wrapper">
                        {/* O overlay pertence à raiz para cobrir também paginação e seletor de registros. */}
                        {shouldShowLoading && (
                            <div className="grid-custom-loading-overlay">
                                <div className="ag-custom-loading">
                                    <i className="fas fa-spinner fa-spin"></i>
                                    <span>{t('loading_data')}</span>
                                </div>
                            </div>
                        )}
                        <div className="card h-auto">
                            <div className="card-body p-0">
                                <div className="table-responsive" style={{ height: height, overflow: 'auto', position: 'relative' }}>
                                    {children}
                                </div>
                                <div className="pagination-footer d-flex align-items-center justify-content-between flex-wrap px-4 py-3">
                                    <div className="dataTables_info d-flex align-items-center gap-3">
                                        {(showPages()) && (
                                            <p className="mb-0">
                                                {t('showing')} <strong>{((pageable.size * pageable.number) + 1)}</strong> - <strong>{(pageable.number * pageable.size) + pageable.numberOfElements}</strong> {t('from')} <strong>{pageable.totalElements}</strong>
                                            </p>
                                        )}
                                        {selectedCount > 0 && selectionMode !== 'single' && (
                                            <Badge bg="primary" className="p-2">
                                                {selectedCount} {t('selected')}
                                            </Badge>
                                        )}
                                    </div>
                                    <div className="d-flex align-items-center gap-2">
                                        {(showPages()) && (
                                            <nav>
                                                <ul className="pagination pagination-gutter mb-0">
                                                    {pagination.map((item, index) => <span key={index}>{item}</span>)}
                                                </ul>
                                            </nav>
                                        )}
                                        <Dropdown className="page-dropdown">
                                            <Dropdown.Toggle as="div" className="form-control default-select border i-false" style={{ cursor: 'pointer', width: 'auto', minWidth: 140 }}>
                                                {rowsPerPageText !== null ? rowsPerPageText : '25 ' + t('records_per_page')} <i className="fas fa-chevron-down ms-2"></i>
                                            </Dropdown.Toggle>
                                            <Dropdown.Menu>
                                                <Dropdown.Item onClick={() => { setSize(10); setRowsPerPageText('10 ' + t('records_per_page')) }}>10 {t('records_per_page')}</Dropdown.Item>
                                                <Dropdown.Item onClick={() => { setSize(25); setRowsPerPageText('25 ' + t('records_per_page')) }}>25 {t('records_per_page')}</Dropdown.Item>
                                                <Dropdown.Item onClick={() => { setSize(50); setRowsPerPageText('50 ' + t('records_per_page')) }}>50 {t('records_per_page')}</Dropdown.Item>
                                                <Dropdown.Item onClick={() => { setSize(100); setRowsPerPageText('100 ' + t('records_per_page')) }}>100 {t('records_per_page')}</Dropdown.Item>
                                            </Dropdown.Menu>
                                        </Dropdown>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

export default GridCustomBase;
